<template>
  <div class="admin-layout">
    <AppHeader />

    <div class="admin-container">
      <!-- サイドバー -->
      <aside class="admin-sidebar">
        <div class="sidebar-header">
          <span class="sidebar-icon">⚙️</span>
          <h2 class="sidebar-title">管理者パネル</h2>
        </div>
        <nav class="sidebar-nav">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="nav-item"
            :class="{ active: activeTab === tab.key }"
            @click="switchTab(tab.key)"
          >
            <span class="nav-icon">{{ tab.icon }}</span>
            <span class="nav-label">{{ tab.label }}</span>
          </button>
        </nav>
      </aside>

      <!-- メインコンテンツ -->
      <main class="admin-main">
        <router-link v-if="publicContentEnabled" class="public-content-link" to="/admin/public-contents">
          公開コンテンツ編集部を開く
        </router-link>
        <!-- ダッシュボード -->
        <section v-if="activeTab === 'dashboard'">
          <div class="section-header">
            <h1 class="section-title">ダッシュボード</h1>
          </div>
          <div v-if="adminStore.isLoading" class="loading-state">
            <div class="spinner"></div>
            読み込み中...
          </div>
          <div v-else class="stats-grid">
            <div class="stat-card">
              <div class="stat-icon">👥</div>
              <div class="stat-value">
                {{ adminStore.stats?.total_users ?? "-" }}
              </div>
              <div class="stat-label">総ユーザー数</div>
            </div>
            <div class="stat-card">
              <div class="stat-icon">📖</div>
              <div class="stat-value">
                {{ adminStore.stats?.total_shiori ?? "-" }}
              </div>
              <div class="stat-label">総しおり数</div>
            </div>
            <div class="stat-card">
              <div class="stat-icon">📢</div>
              <div class="stat-value">
                {{ adminStore.stats?.active_announcements ?? "-" }}
              </div>
              <div class="stat-label">公開中のお知らせ</div>
            </div>
            <div class="stat-card">
              <div class="stat-icon">🔔</div>
              <div class="stat-value">
                {{ adminStore.stats?.total_notifications ?? "-" }}
              </div>
              <div class="stat-label">送信済み通知数</div>
            </div>
          </div>
        </section>

        <!-- ユーザー管理 -->
        <section v-if="activeTab === 'users'">
          <div class="section-header">
            <h1 class="section-title">ユーザー管理</h1>
          </div>
          <div v-if="adminStore.isLoading" class="loading-state">
            <div class="spinner"></div>
            読み込み中...
          </div>
          <div v-else>
            <!-- 検索バー -->
            <div class="toolbar">
              <input
                v-model="userSearch"
                class="filter-input"
                placeholder="名前またはメールで検索..."
                @input="userPage = 1"
              />
              <span class="result-count">{{ filteredUsers.length }} 件</span>
            </div>
            <div class="table-wrapper">
              <table class="admin-table">
                <thead>
                  <tr>
                    <th>ユーザー名</th>
                    <th>メールアドレス</th>
                    <th>登録日</th>
                    <th>管理者</th>
                    <th>有料プラン</th>
                    <th>削除済み</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="u in pagedUsers" :key="u.APP_USER_CODE">
                    <td>{{ u.USER_NAME }}</td>
                    <td>{{ u.EMAIL }}</td>
                    <td>{{ formatDate(u.ENTRY_YMD) }}</td>
                    <td>
                      <span
                        :class="[
                          'badge',
                          u.ADMIN_FLG === '1' ? 'badge-admin' : 'badge-normal',
                        ]"
                      >
                        {{ u.ADMIN_FLG === "1" ? "管理者" : "一般" }}
                      </span>
                    </td>
                    <td>
                      <span
                        :class="[
                          'badge',
                          u.SUBSCRIPTION_FLG === '1'
                            ? 'badge-paid'
                            : 'badge-free',
                        ]"
                      >
                        {{ u.SUBSCRIPTION_FLG === "1" ? "有料" : "無料" }}
                      </span>
                    </td>
                    <td>
                      <span
                        :class="[
                          'badge',
                          u.DELETE_FLG === '1'
                            ? 'badge-deleted'
                            : 'badge-active',
                        ]"
                      >
                        {{ u.DELETE_FLG === "1" ? "削除済" : "正常" }}
                      </span>
                    </td>
                    <td class="actions-cell">
                      <button
                        class="btn-sm btn-outline"
                        @click="toggleAdmin(u)"
                        :disabled="u.APP_USER_CODE === currentUserCode"
                      >
                        {{ u.ADMIN_FLG === "1" ? "管理者解除" : "管理者付与" }}
                      </button>
                      <button
                        class="btn-sm btn-outline"
                        @click="toggleSubscription(u)"
                      >
                        {{
                          u.SUBSCRIPTION_FLG === "1" ? "有料解除" : "有料付与"
                        }}
                      </button>
                      <button
                        v-if="
                          u.DELETE_FLG !== '1' &&
                          u.APP_USER_CODE !== currentUserCode
                        "
                        class="btn-sm btn-danger"
                        @click="confirmDeleteUser(u)"
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- ページネーション -->
            <div class="pagination" v-if="userTotalPages > 1">
              <button
                class="page-btn"
                :disabled="userPage === 1"
                @click="userPage = 1"
              >
                «
              </button>
              <button
                class="page-btn"
                :disabled="userPage === 1"
                @click="userPage--"
              >
                ‹
              </button>
              <template v-for="p in userTotalPages" :key="p">
                <button
                  v-if="Math.abs(p - userPage) <= 2"
                  class="page-btn"
                  :class="{ active: p === userPage }"
                  @click="userPage = p"
                >
                  {{ p }}
                </button>
              </template>
              <button
                class="page-btn"
                :disabled="userPage === userTotalPages"
                @click="userPage++"
              >
                ›
              </button>
              <button
                class="page-btn"
                :disabled="userPage === userTotalPages"
                @click="userPage = userTotalPages"
              >
                »
              </button>
              <span class="page-info">{{ userPageRangeLabel }}</span>
            </div>
          </div>
        </section>

        <!-- マスターデータ -->
        <section v-if="activeTab === 'master'">
          <div class="section-header">
            <h1 class="section-title">マスターデータ編集</h1>
          </div>

          <!-- マスターサブタブ -->
          <div class="sub-tabs">
            <button
              class="sub-tab"
              :class="{ active: masterSubTab === 'prefecture' }"
              @click="masterSubTab = 'prefecture'"
            >
              🗾 都道府県
            </button>
            <button
              class="sub-tab"
              :class="{ active: masterSubTab === 'country' }"
              @click="masterSubTab = 'country'"
            >
              🌍 国
            </button>
            <button
              class="sub-tab"
              :class="{ active: masterSubTab === 'schedule_category' }"
              @click="masterSubTab = 'schedule_category'"
            >
              📋 スケジュールカテゴリ
            </button>
          </div>

          <!-- 都道府県マスター -->
          <div v-if="masterSubTab === 'prefecture'">
            <div class="toolbar">
              <input
                v-model="prefSearch"
                class="filter-input"
                placeholder="都道府県名で検索..."
              />
              <button class="btn-primary" @click="openPrefModal()">
                ＋ 追加
              </button>
            </div>
            <div class="table-wrapper">
              <table class="admin-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>都道府県名</th>
                    <th>表示順</th>
                    <th>状態</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in filteredPrefectures" :key="p.PREFECTURE_NO">
                    <td>{{ p.PREFECTURE_NO }}</td>
                    <td>{{ p.PREFECTURE_NMV }}</td>
                    <td>{{ p.SORT_ORDER }}</td>
                    <td>
                      <span
                        :class="[
                          'badge',
                          p.DELETE_FLG === '1'
                            ? 'badge-deleted'
                            : 'badge-active',
                        ]"
                      >
                        {{ p.DELETE_FLG === "1" ? "無効" : "有効" }}
                      </span>
                    </td>
                    <td class="actions-cell">
                      <button
                        class="btn-sm btn-outline"
                        @click="openPrefModal(p)"
                      >
                        編集
                      </button>
                      <button
                        v-if="p.DELETE_FLG !== '1'"
                        class="btn-sm btn-danger"
                        @click="deletePrefecture(p.PREFECTURE_NO)"
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- 国マスター -->
          <div v-if="masterSubTab === 'country'">
            <div class="toolbar">
              <input
                v-model="countrySearch"
                class="filter-input"
                placeholder="国名で検索..."
              />
              <button class="btn-primary" @click="openCountryModal()">
                ＋ 追加
              </button>
            </div>
            <div class="table-wrapper">
              <table class="admin-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>国名</th>
                    <th>表示順</th>
                    <th>状態</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in filteredCountries" :key="c.COUNTRY_NO">
                    <td>{{ c.COUNTRY_NO }}</td>
                    <td>{{ c.COUNTRY_NMV }}</td>
                    <td>{{ c.SORT_ORDER }}</td>
                    <td>
                      <span
                        :class="[
                          'badge',
                          c.DELETE_FLG === '1'
                            ? 'badge-deleted'
                            : 'badge-active',
                        ]"
                      >
                        {{ c.DELETE_FLG === "1" ? "無効" : "有効" }}
                      </span>
                    </td>
                    <td class="actions-cell">
                      <button
                        class="btn-sm btn-outline"
                        @click="openCountryModal(c)"
                      >
                        編集
                      </button>
                      <button
                        v-if="c.DELETE_FLG !== '1'"
                        class="btn-sm btn-danger"
                        @click="deleteCountry(c.COUNTRY_NO)"
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- スケジュールカテゴリマスター -->
          <div v-if="masterSubTab === 'schedule_category'">
            <div class="toolbar">
              <button class="btn-primary" @click="openScheduleCategoryModal()">
                ＋ 追加
              </button>
            </div>
            <div class="table-wrapper">
              <table class="admin-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>コード</th>
                    <th>カテゴリ名</th>
                    <th>アイコン</th>
                    <th>表示順</th>
                    <th>状態</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="cat in adminStore.scheduleCategories"
                    :key="cat.CATEGORY_NO"
                  >
                    <td>{{ cat.CATEGORY_NO }}</td>
                    <td>
                      <code>{{ cat.CATEGORY_CDV }}</code>
                    </td>
                    <td>{{ cat.CATEGORY_NMV }}</td>
                    <td>
                      <code>{{ cat.ICON_NMV }}</code>
                    </td>
                    <td>{{ cat.SORT_ORDER }}</td>
                    <td>
                      <span
                        :class="[
                          'badge',
                          cat.DELETE_FLG === '1'
                            ? 'badge-deleted'
                            : 'badge-active',
                        ]"
                      >
                        {{ cat.DELETE_FLG === "1" ? "無効" : "有効" }}
                      </span>
                    </td>
                    <td class="actions-cell">
                      <button
                        class="btn-sm btn-outline"
                        @click="openScheduleCategoryModal(cat)"
                      >
                        編集
                      </button>
                      <button
                        v-if="cat.DELETE_FLG !== '1'"
                        class="btn-sm btn-danger"
                        @click="deleteScheduleCategory(cat.CATEGORY_NO)"
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <!-- 通知送信 -->
        <section v-if="activeTab === 'notifications'">
          <div class="section-header">
            <h1 class="section-title">通知送信</h1>
          </div>

          <!-- 通知作成フォーム -->
          <div class="form-card">
            <h2 class="form-title">新規通知を送信</h2>
            <div class="form-group">
              <label class="form-label"
                >タイトル <span class="required">*</span></label
              >
              <input
                v-model="notifForm.TITLE"
                class="form-input"
                placeholder="通知タイトル"
              />
            </div>
            <div class="form-group">
              <label class="form-label"
                >本文 <span class="required">*</span></label
              >
              <textarea
                v-model="notifForm.BODY_TEXT"
                class="form-textarea"
                rows="4"
                placeholder="通知内容"
              ></textarea>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">種別</label>
                <select
                  v-model="notifForm.NOTIFICATION_TYPE"
                  class="form-select"
                >
                  <option value="INFO">お知らせ (INFO)</option>
                  <option value="WARNING">警告 (WARNING)</option>
                  <option value="SYSTEM">システム (SYSTEM)</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">送信対象</label>
                <select v-model="notifForm.TARGET_TYPE" class="form-select">
                  <option value="ALL">全ユーザー</option>
                  <option value="SPECIFIC">特定ユーザー</option>
                </select>
              </div>
            </div>
            <div v-if="notifForm.TARGET_TYPE === 'SPECIFIC'" class="form-group">
              <label class="form-label">対象ユーザーコード</label>
              <input
                v-model="notifForm.TARGET_USER_CODE"
                class="form-input"
                placeholder="APP_USER_CODE"
              />
            </div>
            <button
              class="btn-primary"
              :disabled="
                !notifForm.TITLE || !notifForm.BODY_TEXT || sendingNotif
              "
              @click="sendNotification"
            >
              {{ sendingNotif ? "送信中..." : "📤 通知を送信" }}
            </button>
          </div>

          <!-- 送信履歴 -->
          <div class="section-subheader">
            <h2 class="section-subtitle">送信履歴</h2>
          </div>
          <div class="table-wrapper">
            <table class="admin-table">
              <thead>
                <tr>
                  <th>タイトル</th>
                  <th>種別</th>
                  <th>送信対象</th>
                  <th>送信日</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="n in adminStore.notifications"
                  :key="n.NOTIFICATION_NO"
                >
                  <td>{{ n.TITLE }}</td>
                  <td>
                    <span
                      :class="[
                        'badge',
                        `badge-notif-${n.NOTIFICATION_TYPE.toLowerCase()}`,
                      ]"
                    >
                      {{ n.NOTIFICATION_TYPE }}
                    </span>
                  </td>
                  <td>
                    {{ n.TARGET_TYPE === "ALL" ? "全員" : n.TARGET_USER_CODE }}
                  </td>
                  <td>{{ formatDate(n.ENTRY_YMD) }}</td>
                  <td>
                    <button
                      class="btn-sm btn-danger"
                      @click="deleteNotification(n.NOTIFICATION_NO)"
                    >
                      削除
                    </button>
                  </td>
                </tr>
                <tr v-if="adminStore.notifications.length === 0">
                  <td colspan="5" class="empty-row">
                    送信済みの通知はありません
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- お知らせ -->
        <section v-if="activeTab === 'announcements'">
          <div class="section-header">
            <h1 class="section-title">お知らせ管理</h1>
            <button class="btn-primary" @click="openAnnouncementModal()">
              ＋ 新規作成
            </button>
          </div>
          <div class="table-wrapper">
            <table class="admin-table">
              <thead>
                <tr>
                  <th>タイトル</th>
                  <th>公開状態</th>
                  <th>公開日</th>
                  <th>作成日</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="a in adminStore.announcements"
                  :key="a.ANNOUNCEMENT_NO"
                >
                  <td>{{ a.TITLE }}</td>
                  <td>
                    <span
                      :class="[
                        'badge',
                        a.PUBLIC_FLG === '1' ? 'badge-active' : 'badge-draft',
                      ]"
                    >
                      {{ a.PUBLIC_FLG === "1" ? "公開中" : "下書き" }}
                    </span>
                  </td>
                  <td>{{ formatDate(a.PUBLISH_YMD) }}</td>
                  <td>{{ formatDate(a.ENTRY_YMD) }}</td>
                  <td class="actions-cell">
                    <button
                      class="btn-sm btn-outline"
                      @click="openAnnouncementModal(a)"
                    >
                      編集
                    </button>
                    <button
                      class="btn-sm btn-outline"
                      @click="toggleAnnouncementPublic(a)"
                    >
                      {{ a.PUBLIC_FLG === "1" ? "非公開にする" : "公開する" }}
                    </button>
                    <button
                      class="btn-sm btn-danger"
                      @click="deleteAnnouncement(a.ANNOUNCEMENT_NO)"
                    >
                      削除
                    </button>
                  </td>
                </tr>
                <tr v-if="adminStore.announcements.length === 0">
                  <td colspan="5" class="empty-row">お知らせはありません</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- 利用規約 -->
        <section v-if="activeTab === 'terms'">
          <div class="section-header">
            <h1 class="section-title">利用規約管理</h1>
            <button class="btn-primary" @click="openTermsModal()">
              ＋ 新バージョン作成
            </button>
          </div>
          <div class="table-wrapper">
            <table class="admin-table">
              <thead>
                <tr>
                  <th>バージョン</th>
                  <th>状態</th>
                  <th>発効日</th>
                  <th>作成日</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="t in adminStore.terms" :key="t.TERMS_VERSION">
                  <tr>
                    <td>
                      <code>{{ t.TERMS_VERSION }}</code>
                    </td>
                    <td>
                      <span
                        :class="[
                          'badge',
                          t.IS_ACTIVE_FLG === '1'
                            ? 'badge-active'
                            : 'badge-draft',
                        ]"
                      >
                        {{ t.IS_ACTIVE_FLG === "1" ? "適用中" : "未適用" }}
                      </span>
                    </td>
                    <td>{{ formatDate(t.EFFECTIVE_YMD) }}</td>
                    <td>{{ formatDate(t.ENTRY_YMD) }}</td>
                    <td class="actions-cell">
                      <button
                        class="btn-sm btn-outline"
                        @click="toggleTermsPreview(t.TERMS_VERSION)"
                      >
                        {{
                          termsPreview === t.TERMS_VERSION
                            ? "▲ 閉じる"
                            : "▼ 本文"
                        }}
                      </button>
                      <button
                        class="btn-sm btn-outline"
                        @click="openTermsModal(t)"
                      >
                        編集
                      </button>
                      <button
                        v-if="t.IS_ACTIVE_FLG !== '1'"
                        class="btn-sm btn-outline"
                        @click="activateTerms(t.TERMS_VERSION)"
                      >
                        適用する
                      </button>
                      <button
                        class="btn-sm btn-danger"
                        @click="deleteTerms(t.TERMS_VERSION)"
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                  <tr v-if="termsPreview === t.TERMS_VERSION">
                    <td colspan="5" class="terms-preview-cell">
                      <pre class="terms-preview-content">{{
                        t.CONTENT_TEXT
                      }}</pre>
                    </td>
                  </tr>
                </template>
                <tr v-if="adminStore.terms.length === 0">
                  <td colspan="5" class="empty-row">利用規約はありません</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- プライバシーポリシー -->
        <section v-if="activeTab === 'privacy'">
          <div class="section-header">
            <h1 class="section-title">プライバシーポリシー管理</h1>
            <button class="btn-primary" @click="openPrivacyModal()">
              ＋ 新バージョン作成
            </button>
          </div>
          <div class="table-wrapper">
            <table class="admin-table">
              <thead>
                <tr>
                  <th>バージョン</th>
                  <th>状態</th>
                  <th>発効日</th>
                  <th>作成日</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="p in adminStore.privacyPolicies"
                  :key="p.POLICY_VERSION"
                >
                  <tr>
                    <td>
                      <code>{{ p.POLICY_VERSION }}</code>
                    </td>
                    <td>
                      <span
                        :class="[
                          'badge',
                          p.IS_ACTIVE_FLG === '1'
                            ? 'badge-active'
                            : 'badge-draft',
                        ]"
                      >
                        {{ p.IS_ACTIVE_FLG === "1" ? "適用中" : "未適用" }}
                      </span>
                    </td>
                    <td>{{ formatDate(p.EFFECTIVE_YMD) }}</td>
                    <td>{{ formatDate(p.ENTRY_YMD) }}</td>
                    <td class="actions-cell">
                      <button
                        class="btn-sm btn-outline"
                        @click="togglePrivacyPreview(p.POLICY_VERSION)"
                      >
                        {{
                          privacyPreview === p.POLICY_VERSION
                            ? "▲ 閉じる"
                            : "▼ 本文"
                        }}
                      </button>
                      <button
                        class="btn-sm btn-outline"
                        @click="openPrivacyModal(p)"
                      >
                        編集
                      </button>
                      <button
                        v-if="p.IS_ACTIVE_FLG !== '1'"
                        class="btn-sm btn-outline"
                        @click="activatePrivacy(p.POLICY_VERSION)"
                      >
                        適用する
                      </button>
                      <button
                        class="btn-sm btn-danger"
                        @click="deletePrivacy(p.POLICY_VERSION)"
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                  <tr v-if="privacyPreview === p.POLICY_VERSION">
                    <td colspan="5" class="terms-preview-cell">
                      <pre class="terms-preview-content">{{
                        p.CONTENT_TEXT
                      }}</pre>
                    </td>
                  </tr>
                </template>
                <tr v-if="adminStore.privacyPolicies.length === 0">
                  <td colspan="5" class="empty-row">
                    プライバシーポリシーはありません
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Q&A管理 -->
        <section v-if="activeTab === 'qa'">
          <div class="section-header">
            <h1 class="section-title">Q&A管理</h1>
            <button class="btn-primary" @click="openQaModal()">＋ 追加</button>
          </div>
          <div class="table-wrapper">
            <table class="admin-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>質問</th>
                  <th>表示順</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="q in adminStore.qaItems" :key="q.QA_NO">
                  <tr>
                    <td>{{ q.QA_NO }}</td>
                    <td>{{ q.QUESTION_TEXT }}</td>
                    <td>{{ q.SORT_ORDER }}</td>
                    <td class="actions-cell">
                      <button
                        class="btn-sm btn-outline"
                        @click="toggleQaPreview(q.QA_NO)"
                      >
                        {{ qaPreview === q.QA_NO ? "▲ 閉じる" : "▼ 内容" }}
                      </button>
                      <button
                        class="btn-sm btn-outline"
                        @click="openQaModal(q)"
                      >
                        編集
                      </button>
                      <button
                        class="btn-sm btn-danger"
                        @click="deleteQa(q.QA_NO)"
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                  <tr v-if="qaPreview === q.QA_NO">
                    <td colspan="4" class="terms-preview-cell">
                      <p><strong>Q.</strong> {{ q.QUESTION_TEXT }}</p>
                      <p><strong>A.</strong> {{ q.ANSWER_TEXT }}</p>
                    </td>
                  </tr>
                </template>
                <tr v-if="adminStore.qaItems.length === 0">
                  <td colspan="4" class="empty-row">Q&Aはありません</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>

    <!-- ========== モーダル ========== -->

    <!-- 都道府県モーダル -->
    <div
      v-if="prefModal.show"
      class="modal-overlay"
      @click.self="prefModal.show = false"
    >
      <div class="modal-box">
        <div class="modal-header">
          <h3>{{ prefModal.isEdit ? "都道府県編集" : "都道府県追加" }}</h3>
          <button class="modal-close" @click="prefModal.show = false">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label"
              >都道府県名 <span class="required">*</span></label
            >
            <input
              v-model="prefModal.form.PREFECTURE_NMV"
              class="form-input"
              placeholder="例: 北海道"
            />
          </div>
          <div class="form-group">
            <label class="form-label">表示順</label>
            <input
              v-model.number="prefModal.form.SORT_ORDER"
              class="form-input"
              type="number"
              placeholder="例: 1"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="prefModal.show = false">
            キャンセル
          </button>
          <button class="btn-primary" @click="savePrefecture">
            {{ prefModal.isEdit ? "更新" : "追加" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 国モーダル -->
    <div
      v-if="countryModal.show"
      class="modal-overlay"
      @click.self="countryModal.show = false"
    >
      <div class="modal-box">
        <div class="modal-header">
          <h3>{{ countryModal.isEdit ? "国編集" : "国追加" }}</h3>
          <button class="modal-close" @click="countryModal.show = false">
            ✕
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label"
              >国名 <span class="required">*</span></label
            >
            <input
              v-model="countryModal.form.COUNTRY_NMV"
              class="form-input"
              placeholder="例: アメリカ合衆国"
            />
          </div>
          <div class="form-group">
            <label class="form-label">表示順</label>
            <input
              v-model.number="countryModal.form.SORT_ORDER"
              class="form-input"
              type="number"
              placeholder="例: 1"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="countryModal.show = false">
            キャンセル
          </button>
          <button class="btn-primary" @click="saveCountry">
            {{ countryModal.isEdit ? "更新" : "追加" }}
          </button>
        </div>
      </div>
    </div>

    <!-- お知らせモーダル -->
    <div
      v-if="announcementModal.show"
      class="modal-overlay"
      @click.self="announcementModal.show = false"
    >
      <div class="modal-box modal-wide">
        <div class="modal-header">
          <h3>
            {{ announcementModal.isEdit ? "お知らせ編集" : "お知らせ新規作成" }}
          </h3>
          <button class="modal-close" @click="announcementModal.show = false">
            ✕
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label"
              >タイトル <span class="required">*</span></label
            >
            <input
              v-model="announcementModal.form.TITLE"
              class="form-input"
              placeholder="お知らせのタイトル"
            />
          </div>
          <div class="form-group">
            <label class="form-label"
              >本文 <span class="required">*</span></label
            >
            <textarea
              v-model="announcementModal.form.BODY_TEXT"
              class="form-textarea"
              rows="8"
              placeholder="お知らせの内容 (Markdown対応)"
            ></textarea>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">公開状態</label>
              <select
                v-model="announcementModal.form.PUBLIC_FLG"
                class="form-select"
              >
                <option value="0">下書き</option>
                <option value="1">公開</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">公開日</label>
              <input
                v-model="announcementModal.form.PUBLISH_YMD"
                class="form-input"
                type="date"
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="announcementModal.show = false">
            キャンセル
          </button>
          <button class="btn-primary" @click="saveAnnouncement">
            {{ announcementModal.isEdit ? "更新" : "作成" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 利用規約モーダル -->
    <div
      v-if="termsModal.show"
      class="modal-overlay"
      @click.self="termsModal.show = false"
    >
      <div class="modal-box modal-wide">
        <div class="modal-header">
          <h3>{{ termsModal.isEdit ? "利用規約編集" : "利用規約新規作成" }}</h3>
          <button class="modal-close" @click="termsModal.show = false">
            ✕
          </button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label class="form-label"
                >バージョン <span class="required">*</span></label
              >
              <input
                v-model="termsModal.form.TERMS_VERSION"
                class="form-input"
                :disabled="termsModal.isEdit"
                placeholder="例: v1.0"
              />
            </div>
            <div class="form-group">
              <label class="form-label">発効日</label>
              <input
                v-model="termsModal.form.EFFECTIVE_YMD"
                class="form-input"
                type="date"
              />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label"
              >内容 <span class="required">*</span></label
            >
            <textarea
              v-model="termsModal.form.CONTENT_TEXT"
              class="form-textarea form-textarea-tall"
              rows="16"
              placeholder="利用規約の内容 (Markdown対応)"
            ></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">状態</label>
            <select v-model="termsModal.form.IS_ACTIVE_FLG" class="form-select">
              <option value="0">未適用</option>
              <option value="1">適用中（他のバージョンは自動解除）</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="termsModal.show = false">
            キャンセル
          </button>
          <button class="btn-primary" @click="saveTerms">
            {{ termsModal.isEdit ? "更新" : "作成" }}
          </button>
        </div>
      </div>
    </div>

    <!-- プライバシーポリシーモーダル -->
    <div
      v-if="privacyModal.show"
      class="modal-overlay"
      @click.self="privacyModal.show = false"
    >
      <div class="modal-box modal-wide">
        <div class="modal-header">
          <h3>
            {{
              privacyModal.isEdit
                ? "プライバシーポリシー編集"
                : "プライバシーポリシー新規作成"
            }}
          </h3>
          <button class="modal-close" @click="privacyModal.show = false">
            ✕
          </button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label class="form-label"
                >バージョン <span class="required">*</span></label
              >
              <input
                v-model="privacyModal.form.POLICY_VERSION"
                class="form-input"
                :disabled="privacyModal.isEdit"
                placeholder="例: v1.0"
              />
            </div>
            <div class="form-group">
              <label class="form-label">発効日</label>
              <input
                v-model="privacyModal.form.EFFECTIVE_YMD"
                class="form-input"
                type="date"
              />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label"
              >内容 <span class="required">*</span></label
            >
            <textarea
              v-model="privacyModal.form.CONTENT_TEXT"
              class="form-textarea form-textarea-tall"
              rows="16"
              placeholder="プライバシーポリシーの内容"
            ></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">状態</label>
            <select
              v-model="privacyModal.form.IS_ACTIVE_FLG"
              class="form-select"
            >
              <option value="0">未適用</option>
              <option value="1">適用中（他のバージョンは自動解除）</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="privacyModal.show = false">
            キャンセル
          </button>
          <button class="btn-primary" @click="savePrivacy">
            {{ privacyModal.isEdit ? "更新" : "作成" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Q&Aモーダル -->
    <div
      v-if="qaModal.show"
      class="modal-overlay"
      @click.self="qaModal.show = false"
    >
      <div class="modal-box modal-wide">
        <div class="modal-header">
          <h3>{{ qaModal.isEdit ? "Q&A編集" : "Q&A追加" }}</h3>
          <button class="modal-close" @click="qaModal.show = false">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label"
              >質問 <span class="required">*</span></label
            >
            <input
              v-model="qaModal.form.QUESTION_TEXT"
              class="form-input"
              placeholder="質問文を入力"
            />
          </div>
          <div class="form-group">
            <label class="form-label"
              >回答 <span class="required">*</span></label
            >
            <textarea
              v-model="qaModal.form.ANSWER_TEXT"
              class="form-textarea"
              rows="5"
              placeholder="回答文を入力"
            ></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">表示順</label>
            <input
              v-model.number="qaModal.form.SORT_ORDER"
              class="form-input"
              type="number"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="qaModal.show = false">
            キャンセル
          </button>
          <button class="btn-primary" @click="saveQa">
            {{ qaModal.isEdit ? "更新" : "追加" }}
          </button>
        </div>
      </div>
    </div>

    <!-- スケジュールカテゴリモーダル -->
    <div
      v-if="scheduleCategoryModal.show"
      class="modal-overlay"
      @click.self="scheduleCategoryModal.show = false"
    >
      <div class="modal-box">
        <div class="modal-header">
          <h3>
            {{ scheduleCategoryModal.isEdit ? "カテゴリ編集" : "カテゴリ追加" }}
          </h3>
          <button
            class="modal-close"
            @click="scheduleCategoryModal.show = false"
          >
            ✕
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label"
              >コード <span class="required">*</span></label
            >
            <input
              v-model="scheduleCategoryModal.form.CATEGORY_CDV"
              class="form-input"
              :disabled="scheduleCategoryModal.isEdit"
              placeholder="例: hotel"
            />
          </div>
          <div class="form-group">
            <label class="form-label"
              >カテゴリ名 <span class="required">*</span></label
            >
            <input
              v-model="scheduleCategoryModal.form.CATEGORY_NMV"
              class="form-input"
              placeholder="例: ホテル"
            />
          </div>
          <div class="form-group">
            <label class="form-label">アイコン名</label>
            <input
              v-model="scheduleCategoryModal.form.ICON_NMV"
              class="form-input"
              placeholder="例: bed-solid-full"
            />
          </div>
          <div class="form-group">
            <label class="form-label">表示順</label>
            <input
              v-model.number="scheduleCategoryModal.form.SORT_ORDER"
              class="form-input"
              type="number"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button
            class="btn-secondary"
            @click="scheduleCategoryModal.show = false"
          >
            キャンセル
          </button>
          <button class="btn-primary" @click="saveScheduleCategory">
            {{ scheduleCategoryModal.isEdit ? "更新" : "追加" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 確認ダイアログ -->
    <div v-if="confirmDialog.show" class="modal-overlay">
      <div class="modal-box modal-sm">
        <div class="modal-header">
          <h3>確認</h3>
        </div>
        <div class="modal-body">
          <p>{{ confirmDialog.message }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="confirmDialog.show = false">
            キャンセル
          </button>
          <button class="btn-danger" @click="confirmDialog.onConfirm()">
            実行
          </button>
        </div>
      </div>
    </div>

    <!-- トースト通知 -->
    <div v-if="toast.show" class="toast" :class="`toast-${toast.type}`">
      {{ toast.message }}
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import AppHeader from "@/components/AppHeader.vue";
import { useAdminStore } from "@/store/admin-store";
import { useAuthStore } from "@/store/authStore";
import { PUBLIC_CONTENT_ENABLED } from "@/config/public-content";

export default defineComponent({
  name: "AdminView",
  components: { AppHeader },

  setup() {
    const router = useRouter();
    const adminStore = useAdminStore();
    const authStore = useAuthStore();
    const publicContentEnabled = PUBLIC_CONTENT_ENABLED;

    const currentUserCode = computed(() => authStore.user?.APP_USER_CODE);

    // タブ定義
    const tabs = [
      { key: "dashboard", label: "ダッシュボード", icon: "📊" },
      { key: "users", label: "ユーザー管理", icon: "👥" },
      { key: "master", label: "マスターデータ", icon: "🗂️" },
      { key: "notifications", label: "通知送信", icon: "🔔" },
      { key: "announcements", label: "お知らせ", icon: "📢" },
      { key: "terms", label: "利用規約", icon: "📄" },
      { key: "privacy", label: "プライバシーポリシー", icon: "🔒" },
      { key: "qa", label: "Q&A", icon: "❓" },
    ];
    const activeTab = ref("dashboard");
    const masterSubTab = ref("prefecture");

    // 通知フォーム
    const notifForm = ref({
      TITLE: "",
      BODY_TEXT: "",
      NOTIFICATION_TYPE: "INFO",
      TARGET_TYPE: "ALL",
      TARGET_USER_CODE: "",
    });
    const sendingNotif = ref(false);

    // ユーザーページネーション
    const userPage = ref(1);
    const userPageSize = 10;
    const userSearch = ref("");

    const filteredUsers = computed(() => {
      if (!userSearch.value) return adminStore.users;
      const q = userSearch.value.toLowerCase();
      return adminStore.users.filter(
        (u) =>
          (u.USER_NAME || "").toLowerCase().includes(q) ||
          (u.EMAIL || "").toLowerCase().includes(q),
      );
    });

    const userTotalPages = computed(() =>
      Math.max(1, Math.ceil(filteredUsers.value.length / userPageSize)),
    );

    const pagedUsers = computed(() => {
      const start = (userPage.value - 1) * userPageSize;
      return filteredUsers.value.slice(start, start + userPageSize);
    });

    const userPageRangeLabel = computed(() => {
      const total = filteredUsers.value.length;
      const start = (userPage.value - 1) * userPageSize + 1;
      const end = Math.min(userPage.value * userPageSize, total);
      return `${start}–${end} / ${total} 件`;
    });

    // マスターデータ検索
    const prefSearch = ref("");
    const countrySearch = ref("");

    const filteredPrefectures = computed(() => {
      if (!prefSearch.value) return adminStore.prefectures;
      return adminStore.prefectures.filter((p) =>
        (p.PREFECTURE_NMV || "").includes(prefSearch.value),
      );
    });

    const filteredCountries = computed(() => {
      if (!countrySearch.value) return adminStore.countries;
      return adminStore.countries.filter((c) =>
        (c.COUNTRY_NMV || "").includes(countrySearch.value),
      );
    });

    // モーダル状態
    const prefModal = ref({ show: false, isEdit: false, form: {} });
    const countryModal = ref({ show: false, isEdit: false, form: {} });
    const announcementModal = ref({ show: false, isEdit: false, form: {} });
    const termsModal = ref({ show: false, isEdit: false, form: {} });
    const privacyModal = ref({ show: false, isEdit: false, form: {} });
    const qaModal = ref({ show: false, isEdit: false, form: {} });
    const scheduleCategoryModal = ref({ show: false, isEdit: false, form: {} });

    // 確認ダイアログ
    const confirmDialog = ref({ show: false, message: "", onConfirm: null });

    // トースト
    const toast = ref({ show: false, type: "success", message: "" });

    function showToast(message, type = "success") {
      toast.value = { show: true, type, message };
      setTimeout(() => {
        toast.value.show = false;
      }, 3000);
    }

    function showConfirm(message, onConfirm) {
      confirmDialog.value = {
        show: true,
        message,
        onConfirm: () => {
          confirmDialog.value.show = false;
          onConfirm();
        },
      };
    }

    function formatDate(d) {
      if (!d) return "-";
      return String(d).substring(0, 10);
    }

    async function switchTab(key) {
      activeTab.value = key;
      if (key === "users") userPage.value = 1;
      try {
        if (key === "dashboard") await adminStore.fetchStats();
        else if (key === "users") await adminStore.fetchUsers();
        else if (key === "master") {
          await adminStore.fetchPrefectures();
          await adminStore.fetchCountries();
          await adminStore.fetchScheduleCategories();
        } else if (key === "notifications")
          await adminStore.fetchNotifications();
        else if (key === "announcements") await adminStore.fetchAnnouncements();
        else if (key === "terms") await adminStore.fetchTerms();
        else if (key === "privacy") await adminStore.fetchPrivacyPolicies();
        else if (key === "qa") await adminStore.fetchQaItems();
      } catch {
        showToast("データの取得に失敗しました", "error");
      }
    }

    // ユーザー操作
    async function toggleAdmin(u) {
      try {
        await adminStore.updateUser(u.APP_USER_CODE, {
          ADMIN_FLG: u.ADMIN_FLG === "1" ? "0" : "1",
        });
        showToast("更新しました");
      } catch {
        showToast("更新に失敗しました", "error");
      }
    }

    async function toggleSubscription(u) {
      try {
        await adminStore.updateUser(u.APP_USER_CODE, {
          SUBSCRIPTION_FLG: u.SUBSCRIPTION_FLG === "1" ? "0" : "1",
        });
        showToast("更新しました");
      } catch {
        showToast("更新に失敗しました", "error");
      }
    }

    function confirmDeleteUser(u) {
      showConfirm(`「${u.USER_NAME}」を削除しますか？`, async () => {
        try {
          await adminStore.deleteUser(u.APP_USER_CODE);
          showToast("ユーザーを削除しました");
        } catch {
          showToast("削除に失敗しました", "error");
        }
      });
    }

    // 都道府県マスター
    function openPrefModal(p = null) {
      prefModal.value = {
        show: true,
        isEdit: !!p,
        form: p
          ? {
              PREFECTURE_NO: p.PREFECTURE_NO,
              PREFECTURE_NMV: p.PREFECTURE_NMV,
              SORT_ORDER: p.SORT_ORDER,
            }
          : { PREFECTURE_NMV: "", SORT_ORDER: 0 },
      };
    }

    async function savePrefecture() {
      try {
        if (prefModal.value.isEdit) {
          await adminStore.updatePrefecture(
            prefModal.value.form.PREFECTURE_NO,
            {
              PREFECTURE_NMV: prefModal.value.form.PREFECTURE_NMV,
              SORT_ORDER: prefModal.value.form.SORT_ORDER,
            },
          );
        } else {
          await adminStore.createPrefecture(prefModal.value.form);
        }
        prefModal.value.show = false;
        showToast("保存しました");
      } catch (err) {
        showToast(err.response?.data?.detail || "保存に失敗しました", "error");
      }
    }

    async function deletePrefecture(no) {
      showConfirm("この都道府県を削除しますか？", async () => {
        try {
          await adminStore.deletePrefecture(no);
          showToast("削除しました");
        } catch {
          showToast("削除に失敗しました", "error");
        }
      });
    }

    // 国マスター
    function openCountryModal(c = null) {
      countryModal.value = {
        show: true,
        isEdit: !!c,
        form: c
          ? {
              COUNTRY_NO: c.COUNTRY_NO,
              COUNTRY_NMV: c.COUNTRY_NMV,
              SORT_ORDER: c.SORT_ORDER,
            }
          : { COUNTRY_NMV: "", SORT_ORDER: 0 },
      };
    }

    async function saveCountry() {
      try {
        if (countryModal.value.isEdit) {
          await adminStore.updateCountry(countryModal.value.form.COUNTRY_NO, {
            COUNTRY_NMV: countryModal.value.form.COUNTRY_NMV,
            SORT_ORDER: countryModal.value.form.SORT_ORDER,
          });
        } else {
          await adminStore.createCountry(countryModal.value.form);
        }
        countryModal.value.show = false;
        showToast("保存しました");
      } catch (err) {
        showToast(err.response?.data?.detail || "保存に失敗しました", "error");
      }
    }

    async function deleteCountry(no) {
      showConfirm("この国を削除しますか？", async () => {
        try {
          await adminStore.deleteCountry(no);
          showToast("削除しました");
        } catch {
          showToast("削除に失敗しました", "error");
        }
      });
    }

    // 通知
    async function sendNotification() {
      sendingNotif.value = true;
      try {
        const payload = { ...notifForm.value };
        if (payload.TARGET_TYPE !== "SPECIFIC") delete payload.TARGET_USER_CODE;
        const res = await adminStore.sendNotification(payload);
        showToast(res.message);
        notifForm.value = {
          TITLE: "",
          BODY_TEXT: "",
          NOTIFICATION_TYPE: "INFO",
          TARGET_TYPE: "ALL",
          TARGET_USER_CODE: "",
        };
      } catch (err) {
        showToast(err.response?.data?.detail || "送信に失敗しました", "error");
      } finally {
        sendingNotif.value = false;
      }
    }

    async function deleteNotification(no) {
      showConfirm("この通知を削除しますか？", async () => {
        try {
          await adminStore.deleteNotification(no);
          showToast("削除しました");
        } catch {
          showToast("削除に失敗しました", "error");
        }
      });
    }

    // お知らせ
    function openAnnouncementModal(a = null) {
      announcementModal.value = {
        show: true,
        isEdit: !!a,
        form: a
          ? { ...a }
          : { TITLE: "", BODY_TEXT: "", PUBLIC_FLG: "0", PUBLISH_YMD: "" },
      };
    }

    async function saveAnnouncement() {
      try {
        const form = { ...announcementModal.value.form };
        if (!form.PUBLISH_YMD) delete form.PUBLISH_YMD;
        if (announcementModal.value.isEdit) {
          await adminStore.updateAnnouncement(form.ANNOUNCEMENT_NO, {
            TITLE: form.TITLE,
            BODY_TEXT: form.BODY_TEXT,
            PUBLIC_FLG: form.PUBLIC_FLG,
            PUBLISH_YMD: form.PUBLISH_YMD,
          });
        } else {
          await adminStore.createAnnouncement(form);
        }
        announcementModal.value.show = false;
        showToast("保存しました");
      } catch (err) {
        showToast(err.response?.data?.detail || "保存に失敗しました", "error");
      }
    }

    async function toggleAnnouncementPublic(a) {
      try {
        await adminStore.updateAnnouncement(a.ANNOUNCEMENT_NO, {
          PUBLIC_FLG: a.PUBLIC_FLG === "1" ? "0" : "1",
        });
        showToast("更新しました");
      } catch {
        showToast("更新に失敗しました", "error");
      }
    }

    async function deleteAnnouncement(no) {
      showConfirm("このお知らせを削除しますか？", async () => {
        try {
          await adminStore.deleteAnnouncement(no);
          showToast("削除しました");
        } catch {
          showToast("削除に失敗しました", "error");
        }
      });
    }

    // 利用規約
    const termsPreview = ref(null);

    function toggleTermsPreview(version) {
      termsPreview.value = termsPreview.value === version ? null : version;
    }

    function openTermsModal(t = null) {
      termsModal.value = {
        show: true,
        isEdit: !!t,
        form: t
          ? { ...t }
          : {
              TERMS_VERSION: "",
              CONTENT_TEXT: "",
              IS_ACTIVE_FLG: "0",
              EFFECTIVE_YMD: "",
            },
      };
    }

    async function saveTerms() {
      try {
        const form = { ...termsModal.value.form };
        if (!form.EFFECTIVE_YMD) delete form.EFFECTIVE_YMD;
        if (termsModal.value.isEdit) {
          await adminStore.updateTerms(form.TERMS_VERSION, {
            CONTENT_TEXT: form.CONTENT_TEXT,
            IS_ACTIVE_FLG: form.IS_ACTIVE_FLG,
            EFFECTIVE_YMD: form.EFFECTIVE_YMD,
          });
        } else {
          await adminStore.createTerms(form);
        }
        termsModal.value.show = false;
        showToast("保存しました");
      } catch (err) {
        showToast(err.response?.data?.detail || "保存に失敗しました", "error");
      }
    }

    async function activateTerms(version) {
      try {
        await adminStore.updateTerms(version, { IS_ACTIVE_FLG: "1" });
        showToast("適用しました");
      } catch {
        showToast("操作に失敗しました", "error");
      }
    }

    async function deleteTerms(version) {
      showConfirm(`バージョン「${version}」を削除しますか？`, async () => {
        try {
          await adminStore.deleteTerms(version);
          showToast("削除しました");
        } catch {
          showToast("削除に失敗しました", "error");
        }
      });
    }

    // プライバシーポリシー
    const privacyPreview = ref(null);

    function togglePrivacyPreview(version) {
      privacyPreview.value = privacyPreview.value === version ? null : version;
    }

    function openPrivacyModal(p = null) {
      privacyModal.value = {
        show: true,
        isEdit: !!p,
        form: p
          ? { ...p }
          : {
              POLICY_VERSION: "",
              CONTENT_TEXT: "",
              IS_ACTIVE_FLG: "0",
              EFFECTIVE_YMD: "",
            },
      };
    }

    async function savePrivacy() {
      try {
        const form = { ...privacyModal.value.form };
        if (!form.EFFECTIVE_YMD) delete form.EFFECTIVE_YMD;
        if (privacyModal.value.isEdit) {
          await adminStore.updatePrivacyPolicy(form.POLICY_VERSION, {
            CONTENT_TEXT: form.CONTENT_TEXT,
            IS_ACTIVE_FLG: form.IS_ACTIVE_FLG,
            EFFECTIVE_YMD: form.EFFECTIVE_YMD,
          });
        } else {
          await adminStore.createPrivacyPolicy(form);
        }
        privacyModal.value.show = false;
        showToast("保存しました");
      } catch (err) {
        showToast(err.response?.data?.detail || "保存に失敗しました", "error");
      }
    }

    async function activatePrivacy(version) {
      try {
        await adminStore.updatePrivacyPolicy(version, { IS_ACTIVE_FLG: "1" });
        showToast("適用しました");
      } catch {
        showToast("操作に失敗しました", "error");
      }
    }

    async function deletePrivacy(version) {
      showConfirm(`バージョン「${version}」を削除しますか？`, async () => {
        try {
          await adminStore.deletePrivacyPolicy(version);
          showToast("削除しました");
        } catch {
          showToast("削除に失敗しました", "error");
        }
      });
    }

    // Q&A
    const qaPreview = ref(null);

    function toggleQaPreview(no) {
      qaPreview.value = qaPreview.value === no ? null : no;
    }

    function openQaModal(q = null) {
      qaModal.value = {
        show: true,
        isEdit: !!q,
        form: q
          ? { ...q }
          : { QUESTION_TEXT: "", ANSWER_TEXT: "", SORT_ORDER: 0 },
      };
    }

    async function saveQa() {
      try {
        const form = { ...qaModal.value.form };
        if (qaModal.value.isEdit) {
          await adminStore.updateQaItem(form.QA_NO, {
            QUESTION_TEXT: form.QUESTION_TEXT,
            ANSWER_TEXT: form.ANSWER_TEXT,
            SORT_ORDER: form.SORT_ORDER,
          });
        } else {
          await adminStore.createQaItem(form);
        }
        qaModal.value.show = false;
        showToast("保存しました");
      } catch (err) {
        showToast(err.response?.data?.detail || "保存に失敗しました", "error");
      }
    }

    async function deleteQa(no) {
      showConfirm("このQ&Aを削除しますか？", async () => {
        try {
          await adminStore.deleteQaItem(no);
          showToast("削除しました");
        } catch {
          showToast("削除に失敗しました", "error");
        }
      });
    }

    // スケジュールカテゴリ
    function openScheduleCategoryModal(cat = null) {
      scheduleCategoryModal.value = {
        show: true,
        isEdit: !!cat,
        form: cat
          ? { ...cat }
          : { CATEGORY_CDV: "", CATEGORY_NMV: "", ICON_NMV: "", SORT_ORDER: 0 },
      };
    }

    async function saveScheduleCategory() {
      try {
        const form = { ...scheduleCategoryModal.value.form };
        if (scheduleCategoryModal.value.isEdit) {
          await adminStore.updateScheduleCategory(form.CATEGORY_NO, {
            CATEGORY_CDV: form.CATEGORY_CDV,
            CATEGORY_NMV: form.CATEGORY_NMV,
            ICON_NMV: form.ICON_NMV,
            SORT_ORDER: form.SORT_ORDER,
          });
        } else {
          await adminStore.createScheduleCategory(form);
        }
        scheduleCategoryModal.value.show = false;
        showToast("保存しました");
      } catch (err) {
        showToast(err.response?.data?.detail || "保存に失敗しました", "error");
      }
    }

    async function deleteScheduleCategory(no) {
      showConfirm("このカテゴリを削除しますか？", async () => {
        try {
          await adminStore.deleteScheduleCategory(no);
          showToast("削除しました");
        } catch {
          showToast("削除に失敗しました", "error");
        }
      });
    }

    onMounted(async () => {
      // 管理者チェック
      if (!authStore.user) await authStore.fetchUser();
      if (authStore.user?.ADMIN_FLG !== "1") {
        router.push("/mypage");
        return;
      }
      await adminStore.fetchStats();
    });

    return {
      publicContentEnabled,
      tabs,
      activeTab,
      masterSubTab,
      adminStore,
      authStore,
      currentUserCode,
      notifForm,
      sendingNotif,
      prefSearch,
      countrySearch,
      filteredPrefectures,
      filteredCountries,
      userPage,
      userSearch,
      filteredUsers,
      pagedUsers,
      userTotalPages,
      userPageRangeLabel,
      prefModal,
      countryModal,
      announcementModal,
      termsModal,
      confirmDialog,
      toast,
      formatDate,
      switchTab,
      toggleAdmin,
      toggleSubscription,
      confirmDeleteUser,
      openPrefModal,
      savePrefecture,
      deletePrefecture,
      openCountryModal,
      saveCountry,
      deleteCountry,
      sendNotification,
      deleteNotification,
      openAnnouncementModal,
      saveAnnouncement,
      toggleAnnouncementPublic,
      deleteAnnouncement,
      termsPreview,
      toggleTermsPreview,
      openTermsModal,
      saveTerms,
      activateTerms,
      deleteTerms,
      privacyPreview,
      togglePrivacyPreview,
      privacyModal,
      openPrivacyModal,
      savePrivacy,
      activatePrivacy,
      deletePrivacy,
      qaPreview,
      toggleQaPreview,
      qaModal,
      openQaModal,
      saveQa,
      deleteQa,
      scheduleCategoryModal,
      openScheduleCategoryModal,
      saveScheduleCategory,
      deleteScheduleCategory,
    };
  },
});
</script>

<style scoped>
/* ========== レイアウト ========== */
.admin-layout {
  min-height: 100vh;
  background: var(--color-background);
}

.admin-container {
  display: flex;
  min-height: calc(100vh - 64px);
}

/* ========== サイドバー ========== */
.admin-sidebar {
  width: 240px;
  flex-shrink: 0;
  background: var(--color-surface);
  border-right: 1px solid var(--color-border);
  padding: 24px 0;
  position: sticky;
  top: 64px;
  height: calc(100vh - 64px);
  overflow-y: auto;
}

.sidebar-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 20px 20px;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 12px;
}

.sidebar-icon {
  font-size: 1.4rem;
}

.sidebar-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 20px;
  background: none;
  border: none;
  text-align: left;
  font-size: 0.9rem;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: background var(--transition-fast), color var(--transition-fast);
  border-left: 3px solid transparent;
}

.nav-item:hover {
  background: var(--color-background);
  color: var(--color-text);
}

.nav-item.active {
  background: var(--color-primary-light);
  color: var(--primary-hover);
  border-left-color: var(--primary-hover);
  font-weight: 600;
}

.nav-icon {
  font-size: 1.1rem;
  width: 20px;
  text-align: center;
}

/* ========== メインエリア ========== */
.admin-main {
  flex: 1;
  padding: 32px;
  overflow-y: auto;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.section-subheader {
  margin: 32px 0 16px;
}

.section-subtitle {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
}

/* ========== 統計カード ========== */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
}

.stat-card {
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  padding: 24px;
  text-align: center;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border);
}

.stat-icon {
  font-size: 2rem;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-hover);
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.85rem;
  color: var(--color-text-muted);
}

/* ========== サブタブ ========== */
.sub-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 2px solid var(--color-border);
  padding-bottom: 0;
}

.sub-tab {
  padding: 10px 20px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 0.9rem;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
  margin-bottom: -2px;
}

.sub-tab.active {
  color: var(--primary-hover);
  border-bottom-color: var(--primary-hover);
  font-weight: 600;
}

/* ========== ツールバー ========== */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
}

.filter-input {
  padding: 8px 14px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.9rem;
  outline: none;
  flex: 1;
  max-width: 300px;
}

.filter-input:focus {
  border-color: var(--primary-color);
}

.result-count {
  font-size: 0.85rem;
  color: #6b7280;
  white-space: nowrap;
}

/* ページネーション */
.pagination {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 16px;
  flex-wrap: wrap;
}

.page-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  color: #374151;
}

.page-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: var(--primary-color);
}

.page-btn.active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
  font-weight: 700;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  margin-left: 8px;
  font-size: 0.82rem;
  color: #6b7280;
}

/* ========== テーブル ========== */
.table-wrapper {
  overflow-x: auto;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.admin-table th {
  padding: 12px 16px;
  text-align: left;
  background: var(--color-cool-bg);
  color: var(--color-text-muted);
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
}

.admin-table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border-light);
  color: var(--color-text);
  vertical-align: middle;
}

.admin-table tr:last-child td {
  border-bottom: none;
}

.admin-table tr:hover td {
  background: var(--color-background);
}

.empty-row {
  text-align: center;
  color: var(--color-text-light);
  padding: 32px !important;
}

.terms-preview-cell {
  background: var(--color-background);
  padding: 0 !important;
}

.terms-preview-content {
  margin: 0;
  padding: 16px 24px;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 400px;
  overflow-y: auto;
  color: var(--color-text);
  font-family: inherit;
  line-height: 1.6;
}

.actions-cell {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

/* ========== バッジ ========== */
.badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.badge-admin {
  background: #fef3c7;
  color: #92400e;
}
.badge-normal {
  background: var(--color-cool-bg);
  color: var(--color-text-muted);
}
.badge-paid {
  background: #d1fae5;
  color: #065f46;
}
.badge-free {
  background: var(--color-cool-bg);
  color: var(--color-text-muted);
}
.badge-active {
  background: #d1fae5;
  color: #065f46;
}
.badge-deleted {
  background: #fee2e2;
  color: #991b1b;
}
.badge-draft {
  background: #e0e7ff;
  color: #3730a3;
}
.badge-notif-info {
  background: #dbeafe;
  color: #1e40af;
}
.badge-notif-warning {
  background: #fef3c7;
  color: #92400e;
}
.badge-notif-system {
  background: #f3e8ff;
  color: #6b21a8;
}

/* ========== ボタン ========== */
.btn-primary {
  padding: 10px 20px;
  background: var(--primary-color);
  color: #fff;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--transition-fast);
}
.btn-primary:hover {
  background: var(--primary-hover);
}
.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 10px 20px;
  background: var(--color-cool-bg);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--transition-fast);
}
.btn-secondary:hover {
  background: var(--color-border);
}

.btn-danger {
  padding: 10px 20px;
  background: #ef4444;
  color: #fff;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--transition-fast);
}
.btn-danger:hover {
  background: #dc2626;
}

.btn-sm {
  padding: 5px 12px;
  font-size: 0.8rem;
  border-radius: var(--radius-sm);
}

.btn-outline {
  background: none;
  color: var(--primary-hover);
  border: 1px solid var(--primary-color);
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
}
.btn-outline:hover {
  background: var(--color-primary-light);
}
.btn-outline:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ========== フォームカード ========== */
.form-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 28px;
  margin-bottom: 32px;
  max-width: 640px;
  box-shadow: var(--shadow-sm);
}

.form-title {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: var(--color-text);
}

.form-group {
  margin-bottom: 16px;
}

.form-row {
  display: flex;
  gap: 16px;
}

.form-row .form-group {
  flex: 1;
}

.form-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-text-muted);
  margin-bottom: 6px;
}

.required {
  color: #ef4444;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.9rem;
  color: var(--color-text);
  background: var(--color-surface);
  outline: none;
  transition: border-color var(--transition-fast);
  font-family: inherit;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(180, 207, 158, 0.2);
}

.form-input:disabled {
  background: var(--color-cool-bg);
  color: var(--color-text-light);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.form-textarea-tall {
  min-height: 300px;
  font-family: monospace;
  font-size: 0.85rem;
}

/* ========== モーダル ========== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.modal-box {
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-xl);
}

.modal-wide {
  max-width: 720px;
}

.modal-sm {
  max-width: 400px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
}

.modal-header h3 {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: var(--color-text);
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: var(--color-text-muted);
  line-height: 1;
  padding: 4px 8px;
  border-radius: 4px;
}
.modal-close:hover {
  background: var(--color-cool-bg);
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--color-border);
}

/* ========== トースト ========== */
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 14px 24px;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 600;
  z-index: 2000;
  box-shadow: var(--shadow-lg);
  animation: slideIn 0.3s ease-out;
}

.toast-success {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.toast-error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ========== ローディング ========== */
.loading-state {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--color-text-muted);
  padding: 40px 0;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid var(--color-border);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ========== レスポンシブ ========== */
@media (max-width: 768px) {
  .admin-container {
    flex-direction: column;
  }

  .admin-sidebar {
    width: 100%;
    height: auto;
    position: static;
    padding: 12px 0;
  }

  .sidebar-nav {
    display: flex;
    overflow-x: auto;
    gap: 0;
  }

  .nav-item {
    flex-direction: column;
    padding: 10px 16px;
    font-size: 0.75rem;
    border-left: none;
    border-bottom: 3px solid transparent;
    white-space: nowrap;
  }

  .nav-item.active {
    border-left-color: transparent;
    border-bottom-color: var(--primary-hover);
  }

  .admin-main {
    padding: 20px 16px;
  }

  .form-row {
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
